import React from 'react';
import { useDropzone } from 'react-dropzone';
import { type Control, useController } from 'react-hook-form';

import placeholderPng from '@assets/images/placeholder.png';

import Button from '@components/uikit/Button/Button';

import { FILE_SIZE_LIMIT } from '@constants/dropzoneFileLimits';
import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { PROFILE_FIELDS } from '../../constants/schemaProfile';
import MainInfoSkeleton from './MainInfoSkeleton';
import style from './style.module.scss';

interface IProps {
  isLoading: boolean;
  control: Control<any>;
  identityProvider?: string | null;
}

const MainInfo: React.FC<IProps> = ({ isLoading, control, identityProvider }) => {
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();

  const { data: profileData } = useUserProfileQuery();

  const {
    field: { value: imageValue, onChange: imageChange },
  } = useController({ name: PROFILE_FIELDS.IMAGE, control });

  const {
    field: { onChange: removeImageChange },
  } = useController({ name: PROFILE_FIELDS.REMOVE_IMAGE, control });

  const dropzone = useDropzone({
    onDrop: (files) => {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        imageChange(reader.result);
        removeImageChange(false);
      };
      reader.onerror = (error) => console.log(error);
    },
    validator: (file) => {
      if (file.size > FILE_SIZE_LIMIT) {
        showToast({ variant: 'error', message: 'The file is too large.' });
      }

      return null;
    },
    accept: { 'image/*': [] },
    noClick: true,
    noKeyboard: true,
    multiple: false,
  });

  const handleRemove = () => {
    imageChange(null);
    removeImageChange(true);
  };

  return (
    <div className={style.mainInfo}>
      <div className={style.mainInfoTitle}>{t('General')}</div>
      {isLoading ? (
        <MainInfoSkeleton />
      ) : (
        <div className={style.mainInfoFields}>
          <img
            className={style.mainInfoPhoto}
            src={imageValue || placeholderPng}
            alt={profileData?.user?.username}
          />
          <div className={style.mainInfoRight}>
            <div className={style.mainInfoText}>
              <span className={style.mainInfoTextName}>{profileData?.user?.full_name}</span>
              <span className={style.mainInfoTextRole}>{t(profileData?.role)}</span>
            </div>
            <div className={style.mainInfoBtns}>
              {!!imageValue && !imageValue.endsWith('placeholder-user.png') ? (
                <Button type="secondary" text={t('Remove photo')} onClick={handleRemove} />
              ) : (
                <Button type="secondary" text={t('Add photo')} onClick={dropzone.open} />
              )}
              {!identityProvider && (
                <Button
                  type="secondary"
                  text={t('Change password')}
                  onClick={() => setActiveModal({ name: MODAL_NAMES.changePasswordModal })}
                />
              )}
            </div>
          </div>
          <div {...dropzone.getRootProps()}>
            <input {...dropzone.getInputProps()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MainInfo;
